export const isSameUserId = (left, right) =>
    left !== undefined &&
    left !== null &&
    right !== undefined &&
    right !== null &&
    String(left) === String(right)

const hasAssignedOwner = (record, ownerKey) =>
    record?.[ownerKey] !== undefined &&
    record?.[ownerKey] !== null &&
    record?.[ownerKey] !== ''

export const canEditOwnedRecord = (
    record,
    currentUser,
    { creatorKey = 'created_by', ownerKey = 'owner_id' } = {}
) => {
    const currentUserId =
        currentUser && typeof currentUser === 'object'
            ? currentUser.id
            : currentUser

    if (currentUser && typeof currentUser === 'object') {
        if (isSystemAdmin(currentUser)) {
            return true
        }

        if (currentUser.permission_scope === 'legal_entity') {
            if (
                record?.legal_entity_id === undefined ||
                record?.legal_entity_id === null
            ) {
                return true
            }

            return isSameUserId(
                record.legal_entity_id,
                currentUser.active_legal_entity_id
            )
        }
    }

    if (hasAssignedOwner(record, ownerKey)) {
        return isSameUserId(record?.[ownerKey], currentUserId)
    }

    return isSameUserId(record?.[creatorKey], currentUserId)
}

export const disabledOwnedRowClassName =
    'opacity-50 bg-gray-50 text-gray-400'

const SYSTEM_ADMIN_AUTHORITY = '*'

export const isSystemAdmin = (user) =>
    user?.kind === 'system_admin' ||
    user?.permission_scope === 'all_entities' ||
    user?.user_type === 'system_admin'

export const getUserAuthority = (user) => {
    if (!user) return []
    if (isSystemAdmin(user)) return [SYSTEM_ADMIN_AUTHORITY]

    const permissions = Array.isArray(user.permissions)
        ? user.permissions
        : Array.isArray(user.authority)
          ? user.authority
          : []

    const features = user.features || user.capabilities?.features || {}
    const isHeadhuntingEnabled = Boolean(features['services.headhunting'])
    const isHeadhuntingPermission = (permission) =>
        permission.startsWith('headhunting.') ||
        permission.startsWith('services.headhunting_')

    const authority = new Set()
    permissions.forEach((permission) => {
        if (isHeadhuntingPermission(permission) && !isHeadhuntingEnabled) {
            return
        }
        authority.add(permission)
    })

    Object.entries(features).forEach(([key, enabled]) => {
        if (enabled) authority.add(key)
    })

    if (isHeadhuntingEnabled) {
        const canReadRequests =
            permissions.includes('headhunting.request.read') ||
            permissions.includes('services.headhunting_requests.view')
        const canReadCandidates =
            permissions.includes('headhunting.candidate.read') ||
            permissions.includes('services.headhunting_talent_pool.view')
        const canReadApplicants =
            permissions.includes('headhunting.applicant.read') ||
            permissions.includes('services.headhunting_applicants.view')
        const canReadOffers =
            permissions.includes('headhunting.offer.read') ||
            permissions.includes('services.headhunting_offers.view')

        if (canReadRequests) {
            authority.add('services.headhunting_requests.view')
        }
        if (canReadCandidates) {
            authority.add('services.headhunting_talent_pool.view')
        }
        if (canReadApplicants) {
            authority.add('services.headhunting_applicants.view')
        }
        if (canReadOffers) {
            authority.add('services.headhunting_offers.view')
        }
        if (
            canReadRequests ||
            canReadCandidates ||
            canReadApplicants ||
            canReadOffers
        ) {
            authority.add('services.headhunting.access')
        }
    }

    return [...authority]
}

/**
 * Returns true when the authenticated user has the given permission code.
 *
 * The permission set is sourced from `state.auth.user.permissions` which
 * is populated by the auth service after RBAC resolution. When the array
 * is missing (not yet loaded) the helper returns false so permission-
 * gated UI defaults to hidden until the user context is known.
 */
export const userHasPermission = (user, permissionCode) => {
    if (!user || !permissionCode) return false
    if (isSystemAdmin(user)) return true
    const permissionCodes = Array.isArray(permissionCode)
        ? permissionCode.filter(Boolean)
        : [permissionCode]
    if (permissionCodes.length === 0) return false
    const codes = getUserAuthority(user)
    if (!Array.isArray(codes)) return false
    return (
        codes.includes(SYSTEM_ADMIN_AUTHORITY) ||
        permissionCodes.some((code) => codes.includes(code))
    )
}
